import React from 'react';
import PropTypes from 'prop-types';
import { FDBodyText, FDHeadlineText, FDStack } from '@facet-wealth/design';
import IconPicklistOption from './components/IconPicklistOption';

/**
 * Multi-check-picklist component that renders multiple individual selection components.
 * Uses value-based state management similar to BigMultiSelectInput.
 *
 * @param {Array} options - Array of option objects with id, icon, color, prompt, and disabled properties
 * @param {Array | string | number} value - Array of selected option IDs
 * @param {function} onChange - Callback function when selection changes (receives full array of selected IDs)
 * @param {string} title - Title of the picklist
 * @param {string} description - any description of the picklist
 * @param {bool} multiselect - if true, this becomes a multi picklist
 * @param {bool} condensed - if true, the spacing between the options will be reduced
 */
function IconPicklist({
  options,
  value = undefined,
  onChange = undefined,
  title = undefined,
  description = undefined,
  multiSelect = false,
  condensed = false,
}) {
  const handleSingleSelectionChange = (optionId) => {
    if (onChange) {
      onChange(optionId);
    }
  };

  const handleMultiSelectionChange = (optionId, isSelected) => {
    if (onChange) {
      const valueSet = value ? new Set([...value]) : new Set();
      if (isSelected) {
        valueSet.add(optionId);
      } else {
        valueSet.delete(optionId);
      }
      onChange([...valueSet]);
    }
  };

  const handleSelectionChange = (optionId) => (isSelected) => {
    if (multiSelect) {
      handleMultiSelectionChange(optionId, isSelected);
      return;
    }
    handleSingleSelectionChange(optionId);
  };

  return (
    <FDStack direction={'column'} spacing={3} width={'100%'}>
      <FDStack direction={'column'} spacing={2} width={'100%'}>
        {title ? <FDHeadlineText size={'sm'} value={title} color={'textSecondary'} /> : null}
        {description ? <FDBodyText value={description} color={'textDisabled'} /> : null}
      </FDStack>
      <FDStack direction={'column'} spacing={condensed ? 1 : 2} width={'100%'}>
        {options.map((option) => {
          const selected = value && value.includes(option.id);
          return (
            <IconPicklistOption
              key={option.id}
              icon={option.media}
              color={option.color}
              prompt={option.prompt}
              selected={selected}
              disabled={option.disabled}
              showCheckbox={multiSelect}
              onChange={handleSelectionChange(option.id)}
            />
          );
        })}
      </FDStack>
    </FDStack>
  );
}

IconPicklist.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      media: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      prompt: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onChange: PropTypes.func,
  title: PropTypes.string,
  description: PropTypes.string,
  multiSelect: PropTypes.bool,
  condensed: PropTypes.bool,
};

export { IconPicklist };
